// SAD-488 (CAP-04): TanStack Query bindings for zou's GoNoGoService —
// evidence snapshot, append-only record history, and reviewable AI drafts.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { goNoGoClient } from '../../lib/connect/clients';
const GO_NO_GO_KEY = 'go-no-go';
/** Current evidence for the pursuit; nothing is persisted. */ export function useGoNoGoEvidence(captureProjectId) {
    return useQuery({
        queryKey: [
            GO_NO_GO_KEY,
            captureProjectId,
            'evidence'
        ],
        queryFn: async ()=>(await goNoGoClient.getGoNoGoEvidenceSnapshot({
                captureProjectId
            })).snapshot ?? null,
        retry: false
    });
}
/** Every recorded decision, newest first, flagged current per scope. */ export function useGoNoGoRecords(captureProjectId) {
    return useQuery({
        queryKey: [
            GO_NO_GO_KEY,
            captureProjectId,
            'records'
        ],
        queryFn: async ()=>(await goNoGoClient.listGoNoGoRecords({
                captureProjectId
            })).records,
        retry: false
    });
}
function useRefreshGoNoGo() {
    const queryClient = useQueryClient();
    return (captureProjectId)=>queryClient.invalidateQueries({
            queryKey: [
                GO_NO_GO_KEY,
                captureProjectId
            ]
        });
}
export function useRecordGoNoGo(workspaceCorrelationId) {
    const refresh = useRefreshGoNoGo();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>await goNoGoClient.recordGoNoGoDecision({
                ...args,
                aiDraftId: args.aiDraftId ?? ''
            }, workspaceCorrelationId ? {
                headers: {
                    'X-Workspace-Correlation': workspaceCorrelationId
                }
            } : undefined),
        onSuccess: (_resp, vars)=>Promise.all([
                refresh(vars.captureProjectId),
                queryClient.invalidateQueries({
                    queryKey: [
                        'capture-reviews',
                        vars.captureProjectId
                    ]
                }),
                queryClient.invalidateQueries({
                    queryKey: [
                        'pursuit-header'
                    ]
                })
            ])
    });
}
export function useDraftGoNoGo() {
    return useMutation({
        mutationFn: async (args)=>(await goNoGoClient.draftGoNoGoWithAI(args)).draft,
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to draft with AI');
        }
    });
}
export function useDiscardGoNoGoDraft() {
    return useMutation({
        mutationFn: async (args)=>(await goNoGoClient.discardGoNoGoAIDraft(args)).draft
    });
}
